import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { CaretUpDown, Check, X } from "@phosphor-icons/react";

// Jun 2026, user's choice: people are picked from the app's own login list,
// never typed - a typo in an email silently locks somebody out of their own
// register, and nobody finds out until the day they try to use it.
export const PeoplePicker = ({ people, value, onChange, single = false, placeholder, testId, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const selected = value || [];
  const nameOf = (email) => people.find((p) => p.email === email)?.name || email;
  const toggle = (email) => {
    if (single) {
      onChange(selected.includes(email) ? [] : [email]);
      setOpen(false);
      return;
    }
    onChange(selected.includes(email) ? selected.filter((e) => e !== email) : [...selected, email]);
  };
  return (
    <div className="mt-1 space-y-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" disabled={disabled} className="h-9 w-full justify-between font-normal text-sm" data-testid={testId}>
            <span className={`truncate ${selected.length ? "text-[#101828]" : "text-[#98A2B3]"}`}>
              {selected.length ? selected.map(nameOf).join(", ") : placeholder}
            </span>
            <CaretUpDown size={14} className="shrink-0 text-[#667085]" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search name or email" />
            <CommandList>
              <CommandEmpty>Nobody with a login matches. They sign in once, then appear here.</CommandEmpty>
              <CommandGroup>
                {people.map((p) => (
                  <CommandItem
                    key={p.email}
                    value={`${p.name} ${p.email}`}
                    onSelect={() => toggle(p.email)}
                    data-testid={`${testId}-option-${p.email}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#101828] truncate">{p.name}</div>
                      <div className="text-[11px] text-[#667085] truncate">
                        {p.email}
                        {p.is_floor_login && <span className="text-[#0B6B74]"> · floor login</span>}
                        {p.has_register_access === false && <span className="text-[#B54708]"> · no register access yet</span>}
                      </div>
                    </div>
                    {selected.includes(p.email) && <Check size={14} weight="bold" className="text-[#0E7C86] shrink-0" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((email) => (
            <button
              key={email}
              onClick={() => toggle(email)}
              disabled={disabled}
              className="inline-flex items-center gap-1 rounded-full bg-[#F0FDFA] border border-[#99E3DA] px-2 py-0.5 text-[11px] text-[#0B6B74] hover:bg-[#CCFBF1] transition-colors duration-150 disabled:opacity-60"
              data-testid={`${testId}-chip-${email}`}
            >
              {nameOf(email)} {!disabled && <X size={10} weight="bold" />}
            </button>
          ))}
        </div>
      )}
      {selected.some((email) => people.find((p) => p.email === email)?.has_register_access === false) && (
        <p className="text-[11px] text-[#B54708] font-semibold" data-testid={`${testId}-needs-access`}>
          Give them Consumption Register in Access Management - without it they cannot open the register.
        </p>
      )}
    </div>
  );
};
